/**
 * Authentication hooks using Jotai
 */

import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import {
  accessTokenAtom,
  userAtom,
  userPayloadAtom,
  isAuthenticatedAtom,
  syncTokensAtom,
  clearAuthAtom,
  userHydratedAtom,
} from './atoms';
import * as authApi from '../api/auth';
import * as userApi from '../api/users';
import { ApiClientError } from '../api/client';
import { isSmartLinkOnboardingResponse, isSmartLinkAccountExistsResponse } from '../api/types';
import type { TokenDto } from '../api/types';
import { getGoogleRedirectUri, saveOAuthReturnPath } from './oauth';
import { exchangeGoogleOAuthCodeOnce } from './oauth-exchange';
import { prefetchReturnDestination } from './external-redirect';
import { clearSignupOnboarding } from './signup-onboarding';
import { getInviteToken, inviteTokenMatchesEmail } from './invite-pending';
import { getAuthCookie, setAuthCookie } from './session-cookie';
import { decodeAccessTokenPayload } from './jwt';
import { hasPermissionInPayload } from './permissions';
import {
  clearUserBootstrapSnapshot,
  consumeSessionWarmed,
  markSessionWarmed,
  snapshotUserBootstrap,
} from './session-warm';

/** Deduplicate initializeAuth across StrictMode remounts. */
let initializeAuthInflight: Promise<void> | null = null;

async function applyLocalPayload(
  accessToken: string,
  setUserPayload: (payload: ReturnType<typeof decodeAccessTokenPayload>) => void,
): Promise<ReturnType<typeof decodeAccessTokenPayload>> {
  const local = decodeAccessTokenPayload(accessToken);
  if (local) setUserPayload(local);
  return local;
}

/** Deduplicate concurrent refreshAccessToken calls (e.g. parallel 401s). */
let refreshAccessTokenInflight: Promise<string> | null = null;

/**
 * Hook to get authentication state
 */
export function useAuth() {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const accessToken = useAtomValue(accessTokenAtom);
  const user = useAtomValue(userAtom);
  const userPayload = useAtomValue(userPayloadAtom);
  const userHydrated = useAtomValue(userHydratedAtom);
  const setTokens = useSetAtom(syncTokensAtom);
  const clearAuth = useSetAtom(clearAuthAtom);
  const setUser = useSetAtom(userAtom);
  const setUserPayload = useSetAtom(userPayloadAtom);
  const setUserHydrated = useSetAtom(userHydratedAtom);

  const hydrateProfile = useCallback(
    async (token: string) => {
      const userProfile = await userApi.getCurrentUser(token);
      setUser(userProfile);
      clearUserBootstrapSnapshot();
      return userProfile;
    },
    [setUser],
  );

  /**
   * Apply tokens after login/register/OAuth.
   * Uses local JWT decode (no verify-token round-trip) — tokens were just issued.
   *
   * When `deferProfile` is true, skip /users/me so the caller can hard-navigate
   * immediately; the destination app hydrates the profile on boot.
   */
  const completeSession = useCallback(
    async (
      tokens: TokenDto,
      options?: { deferProfile?: boolean },
    ) => {
      setTokens(tokens);
      await applyLocalPayload(tokens.access_token, setUserPayload);
      // Mark warm before any profile network work so handoff redirects are immediate.
      markSessionWarmed();

      if (options?.deferProfile) {
        setUserHydrated(false);
        return null;
      }

      const userProfile = await hydrateProfile(tokens.access_token);
      markSessionWarmed(snapshotUserBootstrap(userProfile));
      setUserHydrated(true);
      return userProfile;
    },
    [setTokens, setUserPayload, hydrateProfile, setUserHydrated],
  );

  /**
   * Login with email and password
   */
  const login = useCallback(
    async (email: string, password: string) => {
      try {
        const inviteToken = getInviteToken();
        const result = await authApi.login({
          email,
          password,
          ...(inviteToken && inviteTokenMatchesEmail(email)
            ? { invite_token: inviteToken }
            : {}),
        });
        if (isSmartLinkOnboardingResponse(result)) {
          return { success: false as const, needsSmartLinkOnboarding: result };
        }
        // LoginPage immediately returnAfterAuth() — do not wait on /users/me.
        await completeSession(result, { deferProfile: true });
        return { success: true };
      } catch (error) {
        if (error instanceof ApiClientError) {
          return { success: false, error: error.message };
        }
        return { success: false, error: 'Login failed' };
      }
    },
    [completeSession],
  );

  /** Finish Smart Link onboarding after the user confirms the previewed profile. */
  const confirmSmartLinkOnboarding = useCallback(
    async (ticket: string) => {
      try {
        const tokens = await authApi.confirmSmartLinkOnboarding(ticket);
        await completeSession(tokens, { deferProfile: true });
        return { success: true };
      } catch (error) {
        if (error instanceof ApiClientError) {
          return { success: false, error: error.message };
        }
        return { success: false, error: 'Failed to link your Smart account' };
      }
    },
    [completeSession],
  );

  /**
   * Register a new user
   */
  const register = useCallback(
    async (
      email: string,
      password: string,
      firstName: string,
      lastName: string,
    ) => {
      try {
        const inviteToken = getInviteToken();
        const result = await authApi.register({
          email,
          password,
          firstName,
          lastName,
          ...(inviteToken && inviteTokenMatchesEmail(email)
            ? { invite_token: inviteToken }
            : {}),
        });
        if (isSmartLinkAccountExistsResponse(result)) {
          return { success: false as const, smartLinkAccountExists: result };
        }
        // Registration stays on account for email/phone setup — need the profile.
        const userProfile = await completeSession(result);
        return { success: true, emailVerified: Boolean(userProfile?.emailVerified) };
      } catch (error) {
        if (error instanceof ApiClientError) {
          return { success: false, error: error.message };
        }
        return { success: false, error: 'Registration failed' };
      }
    },
    [completeSession],
  );

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    try {
      const refreshToken = getAuthCookie('refresh_token');
      if (refreshToken) {
        await authApi.logout(refreshToken);
      }
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API call failed:', error);
    } finally {
      clearSignupOnboarding();
      clearUserBootstrapSnapshot();
      consumeSessionWarmed();
      clearAuth();
    }
  }, [clearAuth]);

  /**
   * Refresh access token
   */
  const refreshAccessToken = useCallback(async () => {
    if (refreshAccessTokenInflight) return refreshAccessTokenInflight;

    // Only the token exchange is deduped. Profile hydrate runs afterward so a
    // 401 during /users/me cannot re-enter this promise (deadlock).
    refreshAccessTokenInflight = (async () => {
      try {
        const refreshTokenValue = getAuthCookie('refresh_token');
        if (!refreshTokenValue) {
          throw new Error('No refresh token available');
        }

        const tokens = await authApi.refreshToken(refreshTokenValue);
        setTokens(tokens);
        await applyLocalPayload(tokens.access_token, setUserPayload);
        return tokens.access_token;
      } catch (error) {
        // If refresh fails, clear auth state
        clearAuth();
        throw error;
      } finally {
        refreshAccessTokenInflight = null;
      }
    })();

    const access = await refreshAccessTokenInflight;
    await hydrateProfile(access);
    setUserHydrated(true);
    return access;
  }, [setTokens, setUserPayload, hydrateProfile, setUserHydrated, clearAuth]);

  /**
   * Redirect to Google OAuth (callback at /auth/google/callback).
   */
  const loginWithGoogle = useCallback(async () => {
    // Warm pro/cert while the user is on Google (and again on callback).
    prefetchReturnDestination();
    const redirectUri = getGoogleRedirectUri();
    saveOAuthReturnPath(window.location.pathname + window.location.search);
    const { url } = await authApi.getGoogleAuthUrl(redirectUri);
    window.location.href = url;
  }, []);

  /**
   * Exchange OAuth code for session (used on callback route).
   * Deduped for StrictMode — auth codes are single-use.
   */
  const completeGoogleOAuth = useCallback(
    (code: string) =>
      exchangeGoogleOAuthCodeOnce(code, async (oauthCode) => {
        const tokens = await authApi.handleGoogleOAuthCallback({
          code: oauthCode,
          redirect_uri: getGoogleRedirectUri(),
        });
        const isNewUser = Boolean(tokens.is_new_user);
        // Returning users hard-navigate to Pro immediately — hydrate /users/me there.
        // New users stay on account for onboarding and need the profile now.
        await completeSession(tokens, { deferProfile: !isNewUser });
        return { success: true as const, isNewUser };
      }),
    [completeSession],
  );

  /**
   * Initialize auth state from stored tokens.
   * Local JWT already hydrated atoms; this background-fetches /users/me.
   * Skips network verify-token when JWT is locally valid or session was just warmed.
   */
  const initializeAuth = useCallback(async () => {
    if (initializeAuthInflight) return initializeAuthInflight;

    initializeAuthInflight = (async () => {
      const token = getAuthCookie('access_token');
      if (!token) {
        setUserHydrated(true);
        return;
      }

      const warmed = consumeSessionWarmed();
      const local = await applyLocalPayload(token, setUserPayload);

      try {
        if (local || warmed) {
          // Unexpired local JWT or fresh handoff — skip verify-token; /users/me validates.
          await hydrateProfile(token);
        } else {
          // Expired/invalid local decode — verify + profile in parallel, else refresh.
          try {
            const [payload, userProfile] = await Promise.all([
              authApi.verifyToken(token),
              userApi.getCurrentUser(token),
            ]);
            setUserPayload(payload);
            setUser(userProfile);
            clearUserBootstrapSnapshot();
          } catch {
            await refreshAccessToken();
            return;
          }
        }
        setUserHydrated(true);
      } catch {
        try {
          await refreshAccessToken();
        } catch {
          clearAuth();
          setUserHydrated(true);
        }
      }
    })().finally(() => {
      initializeAuthInflight = null;
    });

    return initializeAuthInflight;
  }, [
    setUserPayload,
    setUser,
    hydrateProfile,
    refreshAccessToken,
    clearAuth,
    setUserHydrated,
  ]);

  const refreshUser = useCallback(async () => {
    const token = accessToken ?? getAuthCookie('access_token');
    if (!token) return;
    await hydrateProfile(token);
    setUserHydrated(true);
  }, [accessToken, hydrateProfile, setUserHydrated]);

  /** Verify email when a pending org/product invite token is stored. */
  const verifyEmailWithPendingInvite = useCallback(async () => {
    const inviteToken = getInviteToken();
    const currentAccess = accessToken ?? getAuthCookie('access_token');
    if (!inviteToken || !currentAccess) {
      return { success: false as const, error: 'Missing invitation or session' };
    }

    try {
      const userProfile = await userApi.getCurrentUser(currentAccess);
      if (userProfile.emailVerified) {
        try {
          await userApi.claimOrganizationInvite(currentAccess, inviteToken);
        } catch {
          const { acceptPendingProductInvite } = await import(
            './accept-pending-product-invite'
          );
          await acceptPendingProductInvite(currentAccess);
        }
        await refreshUser();
        return { success: true as const };
      }

      const result = await authApi.verifyEmailViaInvite(
        currentAccess,
        inviteToken,
      );
      if (result.access_token) {
        const refresh = getAuthCookie('refresh_token') ?? '';
        const refreshExpires = getAuthCookie('refresh_token_expires_at') ?? '';
        if (refresh && refreshExpires) {
          setTokens({
            access_token: result.access_token,
            refresh_token: refresh,
            refresh_token_expires_at: refreshExpires,
          });
        } else {
          setAuthCookie('access_token', result.access_token);
        }
        await applyLocalPayload(result.access_token, setUserPayload);
      }
      // Product invites are accepted server-side in verifyEmailViaInvite;
      // still try client accept for older servers / already-verified edge cases.
      const { acceptPendingProductInvite } = await import(
        './accept-pending-product-invite'
      );
      await acceptPendingProductInvite(
        result.access_token ?? currentAccess,
      );
      await refreshUser();
      return { success: true as const };
    } catch (error) {
      if (error instanceof ApiClientError) {
        return { success: false as const, error: error.message };
      }
      return { success: false as const, error: 'Email verification failed' };
    }
  }, [accessToken, setTokens, setUserPayload, refreshUser]);

  return {
    isAuthenticated,
    accessToken,
    user,
    userPayload,
    userHydrated,
    login,
    register,
    confirmSmartLinkOnboarding,
    logout,
    clearAuth,
    refreshAccessToken,
    refreshUser,
    verifyEmailWithPendingInvite,
    initializeAuth,
    completeSession,
    loginWithGoogle,
    completeGoogleOAuth,
  };
}

/**
 * Hook to check if user has a specific permission
 */
export function usePermission(permission: string) {
  const userPayload = useAtomValue(userPayloadAtom);
  const accessToken = useAtomValue(accessTokenAtom);

  const hasPermission = useCallback(
    async (): Promise<boolean> => {
      if (!userPayload || !accessToken) {
        return false;
      }

      if (hasPermissionInPayload(userPayload, permission)) {
        return true;
      }

      // If not in payload, check with API (for dynamic permissions)
      try {
        const result = await authApi.checkPermission(accessToken, permission);
        return result.hasPermission;
      } catch {
        return false;
      }
    },
    [userPayload, accessToken, permission],
  );

  // Synchronous check based on payload
  const hasPermissionSync = useCallback((): boolean => {
    return hasPermissionInPayload(userPayload, permission);
  }, [userPayload, permission]);

  return {
    hasPermission,
    hasPermissionSync,
  };
}

/**
 * Hook to check if user has a specific role
 */
export function useRole(role: string) {
  const userPayload = useAtomValue(userPayloadAtom);

  const hasRole = useCallback((): boolean => {
    if (!userPayload) {
      return false;
    }
    return userPayload.roles.includes(role);
  }, [userPayload, role]);

  return { hasRole };
}
